import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { Maximize2, X } from "lucide-mui";
import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link as RouterLink } from "react-router-dom";
import { api, type ProspectListContactEntry } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import Kbd from "../../components/Kbd.tsx";
import PanelResizer from "../../components/PanelResizer.tsx";
import UserAvatar from "../../components/UserAvatar.tsx";
import { DRAWER_EASING, flood, prefersReducedMotion, type FloodOrigin } from "../../components/contacts/flood.ts";
import { useTenant } from "../../contexts/TenantContext.tsx";
import { SHORTCUT_SCOPE, useKeyboardShortcuts } from "../../hooks/useKeyboardShortcuts.ts";
import { useResizableWidth } from "../../hooks/useResizableWidth.ts";
import { useWindowWidth } from "../../hooks/useWindowWidth.ts";
import ContactProfileSections, { contactLocation } from "./components/ContactProfileSections.tsx";

// Its own width, saved per user; narrower than a detail drawer by default.
const WIDTH_PREFERENCE = "contact_drawer.width";
const DEFAULT_WIDTH = 480;
const MIN_WIDTH = 360;
// Page kept visible left of it when it sits beside the drawer.
const LEFT_GUTTER = 16;
// Kept clear at the left when it has to cover the drawer instead.
const EDGE_GUTTER = 48;
// Below MUI's `sm` it fills the screen.
const SM = 600;
// It unfolds out of the drawer's edge; leaving is quicker and folds back in.
const ENTER_MS = 320;
const EXIT_MS = 200;
const EXIT_EASING = "cubic-bezier(0.4, 0, 1, 1)";

interface ContactDrawerProps {
  /** The master contact shown; null closes the drawer. */
  contactId: string | null;
  /** The entry it was opened from: its header shows at once, before the person loads. */
  entry?: ProspectListContactEntry;
  /** How far the drawer it opens from sits from the window's right edge: it docks there. */
  edge: number;
  /** The card it was opened from: its colour floods into the drawer. */
  origin: FloodOrigin | null;
  /** Where it sits among the people it steps through (J / K). */
  position?: { index: number; total: number };
  onStep?: (direction: 1 | -1) => void;
  onClose: () => void;
}

/**
 * A person, opened from a card in a detail drawer: it unfolds out of that
 * drawer's left edge (the drawer itself stays put), a header in the people
 * colour (the card's), then what the contact page shows. Not a modal: the
 * drawer stays usable, and another card there swaps the person. On a window
 * with no room beside the drawer it covers it instead. Esc closes it, J / K
 * step through the people it came from.
 */
export default function ContactDrawer({ contactId, entry, edge, origin, position, onStep, onClose }: ContactDrawerProps) {
  const theme = useTheme();
  const { tenantPath } = useTenant();
  const open = !!contactId;
  // Mounted from opening until it has folded away.
  const [present, setPresent] = useState(open);
  if (open && !present) setPresent(true);
  // The last person stays drawn while it folds away.
  const [shown, setShown] = useState<{ id: string; entry?: ProspectListContactEntry } | null>(null);
  if (contactId && (shown?.id !== contactId || shown.entry !== entry)) setShown({ id: contactId, entry });
  const id = shown?.id ?? null;
  const shownEntry = shown?.entry;

  const windowWidth = useWindowWidth();
  const room = windowWidth - edge - LEFT_GUTTER;
  const beside = windowWidth >= SM && room >= MIN_WIDTH;
  const { width: savedWidth, resizer } = useResizableWidth({
    preference: WIDTH_PREFERENCE,
    defaultWidth: DEFAULT_WIDTH,
    min: MIN_WIDTH,
    max: Math.max(MIN_WIDTH, beside ? room : windowWidth - EDGE_GUTTER),
  });
  const right = beside ? edge : 0;
  const width = windowWidth < SM ? windowWidth : savedWidth;

  const { data, isLoading, error } = useQuery({
    queryKey: keys.masterContact.detail(id ?? ""),
    queryFn: () => api.getMasterContact(id!),
    enabled: !!id,
  });

  const step = (direction: 1 | -1) => () => onStep?.(direction);
  useKeyboardShortcuts(
    { Escape: onClose, j: step(1), ArrowDown: step(1), k: step(-1), ArrowUp: step(-1) },
    open,
  );

  // Opening: the clicked card's colour floods in, or (no card: a link, Back)
  // the panel unfolds from the drawer's edge. Swapping the person: a flood
  // from the new card, else the content fades. Closing: it folds back in.
  const panelRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const played = useRef<number | null>(null);
  const lastId = useRef<string | null>(null);
  useLayoutEffect(() => {
    const panel = panelRef.current;
    const content = contentRef.current;
    const previous = lastId.current;
    lastId.current = contactId;
    if (!panel || !content) return;
    const still = prefersReducedMotion();
    if (!open) {
      if (still) return setPresent(false);
      const fold = panel.animate(
        [
          { clipPath: "inset(0 0 0 0)", transform: "none" },
          { clipPath: "inset(0 0 0 100%)", transform: "translateX(16px)" },
        ],
        { duration: EXIT_MS, easing: EXIT_EASING, fill: "forwards" },
      );
      fold.finished.then(() => setPresent(false), () => undefined);
      // Reopened while folding: it stays.
      return () => fold.cancel();
    }
    // Played once per click: back on this person later (Back, Forward), it unfolds.
    if (origin && origin.contactId === contactId && played.current !== origin.key) {
      played.current = origin.key;
      const w = window.innerWidth;
      flood(
        origin.rect,
        { top: 0, bottom: window.innerHeight, left: w - right - width, right: w - right },
        { zIndex: theme.zIndex.modal + 2, paper: previous ? undefined : panel, content },
      );
      return;
    }
    if (still) return;
    if (!previous) {
      panel.animate(
        [
          { clipPath: "inset(0 0 0 100%)", transform: "translateX(24px)" },
          { clipPath: "inset(0 0 0 0)", transform: "none" },
        ],
        { duration: ENTER_MS, easing: DRAWER_EASING },
      );
    } else if (previous !== contactId) {
      content.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 160, easing: "ease-out" });
    }
    // Only opening, closing, a new person or a new click animates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contactId, origin?.key, present]);

  const name = data?.full_name ?? shownEntry?.contact_name ?? (isLoading ? "" : "Unnamed contact");
  const caption =
    (shownEntry && [shownEntry.title ?? shownEntry.labels.Role, shownEntry.headline].filter(Boolean).join(" · ")) ||
    data?.profile?.headline;
  const location = data ? contactLocation(data) : "";
  const photo = shownEntry?.photo_url ?? data?.profile?.photo_url;

  if (!present) return null;
  return createPortal(
    <Box
      ref={panelRef}
      {...SHORTCUT_SCOPE}
      role="dialog"
      aria-modal={false}
      aria-label={name || "Contact"}
      sx={{
        position: "fixed",
        top: 0,
        bottom: 0,
        right,
        width,
        // Above the drawer's modal and its backdrop, whichever mounted first (a link opens both at once).
        zIndex: (t) => t.zIndex.modal + 1,
        display: "flex",
        bgcolor: "background.paper",
        borderRight: beside ? 1 : 0,
        borderColor: "people.border",
        boxShadow: "-16px 0 40px -24px rgba(0, 0, 0, 0.35)",
        overflow: "hidden",
      }}
    >
      <PanelResizer
        {...resizer}
        label="Resize the contact panel"
        sx={{ position: "absolute", top: 0, bottom: 0, left: 0, zIndex: 3, display: { xs: "none", sm: "flex" } }}
      />
      <Stack sx={{ flexGrow: 1, minWidth: 0, height: "100%" }}>
        <Box sx={{ bgcolor: "people.light", borderBottom: 1, borderColor: "people.border", px: 3, pt: 1.5, pb: 2.5 }}>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
            <Typography variant="overline" sx={{ color: "people.dark", flexGrow: 1 }}>
              Contact
              {position && position.total > 1 && ` · ${position.index + 1} of ${position.total}`}
            </Typography>
            {id && (
              <Tooltip title="Open the full page">
                <IconButton component={RouterLink} to={tenantPath(`/contacts/${id}`)} aria-label="Open the full page">
                  <Maximize2 fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton onClick={onClose} aria-label="Close">
              <X />
            </IconButton>
          </Stack>
          <Stack direction="row" spacing={2} sx={{ alignItems: "center", mt: 0.5 }}>
            <UserAvatar displayName={name || "?"} pictureUrl={photo} size={56} tooltip={false} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h3" component="h2" sx={{ color: "people.dark" }}>
                {name || <Skeleton width={180} />}
              </Typography>
              {caption && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {caption}
                </Typography>
              )}
              {(location || data?.is_deceased) && (
                <Typography variant="caption" component="div" sx={{ color: "text.secondary" }}>
                  {[location, data?.is_deceased && "Deceased"].filter(Boolean).join(" · ")}
                </Typography>
              )}
            </Box>
          </Stack>
          {shownEntry?.inclusion_reason && (
            <Typography variant="body2" sx={{ mt: 1.5 }}>
              {shownEntry.inclusion_reason}
            </Typography>
          )}
          {onStep && position && position.total > 1 && (
            <Typography variant="caption" component="div" sx={{ color: "text.secondary", mt: 1.5 }}>
              <Kbd>J</Kbd>
              <Kbd>↓</Kbd> next · <Kbd>K</Kbd>
              <Kbd>↑</Kbd> previous · <Kbd>Esc</Kbd> close
            </Typography>
          )}
        </Box>
        <Box ref={contentRef} sx={{ flexGrow: 1, minHeight: 0, overflowY: "auto", px: 3, pt: 2.5, pb: 1 }}>
          {isLoading && (
            <Stack spacing={2}>
              <Skeleton variant="rounded" height={120} />
              <Skeleton variant="rounded" height={88} />
              <Skeleton variant="rounded" height={160} />
            </Stack>
          )}
          {error && <Alert severity="error">Could not load this contact.</Alert>}
          {data && <ContactProfileSections contact={data} showPhoto={false} />}
        </Box>
      </Stack>
    </Box>,
    document.body,
  );
}
